import React, { useRef, useEffect } from 'react';
import { gsap } from 'gsap';
import './LoadingSpinner.css';

const LoadingSpinner: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let width = window.innerWidth;
    let height = window.innerHeight;
    const target = { x: width / 2, y: height / 2 };
    let points: any[] = [];
    let animationFrameId: number;

    interface Point {
      x: number;
      y: number;
      originX: number;
      originY: number;
      closest?: Point[];
      circle?: Circle;
      active?: number;
    }

    class Circle {
      pos: Point;
      radius: number;
      color: string;
      active: number;

      constructor(pos: Point, rad: number, color: string) {
        this.pos = pos;
        this.radius = rad;
        this.color = color;
        this.active = 0;
      }

      draw() {
        if (!this.active || !ctx) return;
        ctx.beginPath();
        ctx.arc(this.pos.x, this.pos.y, this.radius, 0, 2 * Math.PI, false);
        ctx.fillStyle = `rgba(156,217,249,${this.active})`;
        ctx.fill();
      }
    }

    const getDistance = (p1: {x: number, y: number}, p2: Point) => {
        return Math.pow(p1.x - p2.x, 2) + Math.pow(p1.y - p2.y, 2);
    }

    const initHeader = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      target.x = width / 2;
      target.y = height / 2;
      
      canvas.width = width;
      canvas.height = height;

      points = [];
      for (let x = 0; x < width; x = x + width / 20) {
        for (let y = 0; y < height; y = y + height / 20) {
          const px = x + (Math.random() * width) / 20;
          const py = y + (Math.random() * height) / 20;
          points.push({ x: px, originX: px, y: py, originY: py });
        }
      }

      for (let i = 0; i < points.length; i++) {
        const closest = [];
        const p1 = points[i];
        for (let j = 0; j < points.length; j++) {
          const p2 = points[j];
          if (p1 !== p2) {
            let placed = false;
            for (let k = 0; k < 5; k++) {
              if (!placed && !closest[k]) {
                closest[k] = p2;
                placed = true;
              }
            }
            for (let k = 0; k < 5; k++) {
              if (!placed && getDistance(p1, p2) < getDistance(p1, closest[k])) {
                closest[k] = p2;
                placed = true;
              }
            }
          }
        }
        p1.closest = closest;
        p1.circle = new Circle(p1, 2 + Math.random() * 2, 'rgba(255,255,255,0.3)');
      }
      
      // Start the animation for each point
      for (const p of points) {
          shiftPoint(p);
      }
    };
    
    const shiftPoint = (p: Point) => {
        gsap.to(p, {
            x: p.originX - 50 + Math.random() * 100,
            y: p.originY - 50 + Math.random() * 100,
            duration: 1 + Math.random(),
            ease: 'circ.inOut',
            onComplete: () => shiftPoint(p),
        });
    };

    const drawLines = (p: Point) => {
      if (!p.active || !p.closest) return;
      for (const closest of p.closest) {
        ctx.beginPath();
        ctx.moveTo(p.x, p.y);
        ctx.lineTo(closest.x, closest.y);
        ctx.strokeStyle = `rgba(156,217,249,${p.active})`;
        ctx.stroke();
      }
    };

    const animate = () => {
      ctx.clearRect(0, 0, width, height);
      for (const p of points) {
        if (Math.abs(getDistance(target, p)) < 4000) {
          p.active = 0.3;
          if (p.circle) p.circle.active = 0.6;
        } else if (Math.abs(getDistance(target, p)) < 20000) {
          p.active = 0.1;
          if (p.circle) p.circle.active = 0.3;
        } else if (Math.abs(getDistance(target, p)) < 40000) {
          p.active = 0.02;
          if (p.circle) p.circle.active = 0.1;
        } else {
          p.active = 0;
          if (p.circle) p.circle.active = 0;
        }
        drawLines(p);
        p.circle?.draw();
      }
      animationFrameId = requestAnimationFrame(animate);
    };

    const mouseMove = (e: MouseEvent) => {
      target.x = e.clientX;
      target.y = e.clientY;
    };
    
    // Add event listeners
    window.addEventListener('mousemove', mouseMove);
    window.addEventListener('resize', initHeader);

    // Initialize and start animation
    initHeader();
    animate();

    // Cleanup function
    return () => {
      window.removeEventListener('mousemove', mouseMove);
      window.removeEventListener('resize', initHeader);
      cancelAnimationFrame(animationFrameId);
      gsap.killTweensOf(points);
    };
  }, []);

  return (
    <div className="loading-spinner-overlay">
      <canvas ref={canvasRef} />
      <div className="loading-text">Cargando...</div>
    </div>
  );
};

export default LoadingSpinner; 